// schemas/customer.ts
import { z } from 'zod'

// ============================================
// 基础 Schema
// ============================================

/** 联系方式类型 Schema */
export const contactTypeSchema = z.union([z.literal(1), z.literal(2)])

/** 联系方式 Key Schema */
export const contactKeySchema = z.enum(['mobile_1', 'mobile_2', 'wechat_1', 'wechat_2'])

/** 联系方式输入 Schema - 可选字段 */
export const contactInputSchema = z.object({
  type: contactTypeSchema,
  key: contactKeySchema,
  value: z
    .string()
    .max(100, '联系方式最多100字符'),
})

/** 联系方式输入 Schema - 必填字段 */
export const contactInputRequiredSchema = z.object({
  type: contactTypeSchema,
  key: contactKeySchema,
  value: z
    .string()
    .min(1, '联系方式不能为空')
    .max(100, '联系方式最多100字符'),
})

// ============================================
// 客户表单 Schema
// ============================================

/** 性别 Schema - 表单中使用字符串，提交时转换为数字 */
export const genderFormSchema = z.union([
  z.literal('0'),
  z.literal('1'),
  z.literal('2'),
]).optional()

/** 创建客户 Schema */
export const createCustomerSchema = z.object({
  name: z
    .string()
    .min(1, '姓名不能为空')
    .max(100, '姓名最多100字符'),
  gender: genderFormSchema,
  tiktok: z.string().max(20, '抖音号最多20字符').optional().or(z.literal('')),
  age: z.number().int('年龄必须为整数').min(0, '年龄不能小于0').optional(),
  job: z.string().max(100, '职业最多100字符').optional().or(z.literal('')),
  family: z.string().optional().or(z.literal('')),
  birthday: z.string().optional().or(z.literal('')),
  blacklist: z.boolean().optional(),
  pic_1: z.string().max(255, '图片地址最多255字符').optional().or(z.literal('')),
  pic_2: z.string().max(255, '图片地址最多255字符').optional().or(z.literal('')),
  province: z.string().max(50, '省份最多50字符').optional().or(z.literal('')),
  city: z.string().max(50, '城市最多50字符').optional().or(z.literal('')),
  district: z.string().max(50, '区县最多50字符').optional().or(z.literal('')),
  city_id: z.number().optional(),
  address: z.string().max(255, '地址最多255字符').optional().or(z.literal('')),
  remark: z.string().optional().or(z.literal('')),
  category_id: z.number().optional(),
  vip_level: z.number().int().optional(),
  contacts: z
    .array(z.object({
      type: contactTypeSchema,
      key: contactKeySchema,
      value: z.string().max(100, '联系方式最多100字符'),
    }))
    .min(1, '至少需要一个联系方式')
    .refine(
      (contacts) => {
        // 手机号1 (index 0) 必填
        const mobile1 = contacts.find(c => c.key === 'mobile_1')
        if (!mobile1 || !mobile1.value.trim()) {
          return false
        }
        return true
      },
      { message: '手机号1为必填项' }
    )
    .refine(
      (contacts) => {
        // 微信号1 (index 2) 必填
        const wechat1 = contacts.find(c => c.key === 'wechat_1')
        if (!wechat1 || !wechat1.value.trim()) {
          return false
        }
        return true
      },
      { message: '微信号1为必填项' }
    ),
})

/** 更新客户 Schema */
export const updateCustomerSchema = createCustomerSchema.partial()

/** 转移客户 Schema */
export const transferCustomerSchema = z.object({
  customer_id: z.number().positive('客户ID必须为正数'),
  user_id: z.number().positive().optional(),
  tracker_id: z.number().optional(),
})

// ============================================
// 检查联系方式 Schema
// ============================================

/** 检查联系方式 Schema */
export const checkContactSchema = z.object({
  type: contactTypeSchema,
  value: z.string().min(1, '联系方式不能为空').max(100),
})

/** 批量检查 Schema */
export const checkContactBatchSchema = z.object({
  type: contactTypeSchema,
  values: z
    .array(z.string().min(1).max(100))
    .min(1, '至少输入一个')
    .max(20000, '最多20000个'),
})

// ============================================
// 类型导出
// ============================================

export type CreateCustomerData = z.infer<typeof createCustomerSchema>
export type UpdateCustomerData = z.infer<typeof updateCustomerSchema>
export type TransferCustomerData = z.infer<typeof transferCustomerSchema>
export type CheckContactData = z.infer<typeof checkContactSchema>
export type CheckContactBatchData = z.infer<typeof checkContactBatchSchema>
export type ContactInputData = z.infer<typeof contactInputSchema>
